import { Suspense } from "react";
import { PortalGate } from "@/components/portal/PortalGate";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams } from "react-router-dom";
import { GaRouteTracker } from "@/components/GaRouteTracker";
import { AuthProvider } from "@/contexts/AuthContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { AIChatProvider } from "@/contexts/AIChatContext";
import { ClientAuthProvider } from "@/contexts/ClientAuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ClientProtectedRoute from "@/components/client/ClientProtectedRoute";
import SubscriptionGate from "@/components/SubscriptionGate";
import { FirstEventGate, FirstEventRouteGate } from "@/components/FirstEventGate";
import { EventLayout } from "@/components/EventLayout";
import { LegacyEventRedirect } from "@/components/event/LegacyEventRedirect";
import { NewVersionBanner } from "@/components/NewVersionBanner";

import "@/lib/i18n";

// Lazy load all pages for code splitting and performance
const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const TermosDeUso = lazyWithRetry(() => import("./pages/TermosDeUso"));
const PoliticaPrivacidade = lazyWithRetry(() => import("./pages/PoliticaPrivacidade"));
const Cookies = lazyWithRetry(() => import("./pages/Cookies"));

const DashboardGeral = lazyWithRetry(() => import("./pages/DashboardGeral"));
const DashboardEventos = lazyWithRetry(() => import("./pages/DashboardEventos"));
const Eventos = lazyWithRetry(() => import("./pages/Eventos"));
const EventoDashboard = lazyWithRetry(() => import("./pages/EventoDashboard"));
const EventoTarefas = lazyWithRetry(() => import("./pages/EventoTarefas"));
const EventoConvidados = lazyWithRetry(() => import("./pages/EventoConvidados"));
const EventoCronograma = lazyWithRetry(() => import("./pages/EventoCronograma"));
const DetalhesEvento = lazyWithRetry(() => import("./pages/DetalhesEvento"));
const TarefasHoje = lazyWithRetry(() => import("./pages/TarefasHoje"));
const TarefasSemana = lazyWithRetry(() => import("./pages/TarefasSemana"));
const Reunioes = lazyWithRetry(() => import("./pages/Reunioes"));
// "Pedidos de orçamento" foi fundido em "Orçamento". A rota antiga redireciona.
const OrcamentosRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/evento/${id}/orcamento`} replace />;
};
const Fornecedores = lazyWithRetry(() => import("./pages/Fornecedores"));
const Documentos = lazyWithRetry(() => import("./pages/Documentos"));
const Configuracoes = lazyWithRetry(() => import("./pages/Configuracoes"));
const Integracoes = lazyWithRetry(() => import("./pages/Integracoes"));

const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Planos = lazyWithRetry(() => import("./pages/Planos"));
const Feedback = lazyWithRetry(() => import("./pages/Feedback"));
const AdminTestadoras = lazyWithRetry(() => import("./pages/AdminTestadoras"));
const Notificacoes = lazyWithRetry(() => import("./pages/Notificacoes"));
const HistoricoPagamentos = lazyWithRetry(() => import("./pages/HistoricoPagamentos"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
// Páginas administrativas da empresa
const Contratos = lazyWithRetry(() => import("./pages/Contratos"));
const Patrimonio = lazyWithRetry(() => import("./pages/Patrimonio"));
const ContasPagar = lazyWithRetry(() => import("./pages/ContasPagar"));
const ContasReceber = lazyWithRetry(() => import("./pages/ContasReceber"));
const OrcamentoEmpresa = lazyWithRetry(() => import("./pages/OrcamentoEmpresa"));
const Vendas = lazyWithRetry(() => import("./pages/Vendas"));
const CRM = lazyWithRetry(() => import("./pages/CRM"));
// Novas páginas de gestão de eventos
const FornecedoresGeral = lazyWithRetry(() => import("./pages/FornecedoresGeral"));
const Clientes = lazyWithRetry(() => import("./pages/Clientes"));
const Modelos = lazyWithRetry(() => import("./pages/Modelos"));
const Briefing = lazyWithRetry(() => import("./pages/Briefing"));
const BriefingFicha = lazyWithRetry(() => import("./pages/BriefingFicha"));
const EventoOrcamento = lazyWithRetry(() => import("./pages/EventoOrcamento"));
const EventoPagamentosFornecedores = lazyWithRetry(() => import("./pages/EventoPagamentosFornecedores"));
const PortalCliente = lazyWithRetry(() => import("./pages/PortalCliente"));
const PortalBriefing = lazyWithRetry(() => import("./pages/PortalBriefing"));
const PortalConvidados = lazyWithRetry(() => import("./pages/PortalConvidados"));
const PortalPropostas = lazyWithRetry(() => import("./pages/PortalPropostas"));
const ClientAuth = lazyWithRetry(() => import("./pages/ClientAuth"));
const ClientPortal = lazyWithRetry(() => import("./pages/ClientPortal"));
const ClientEventDashboard = lazyWithRetry(() => import("./pages/ClientEventDashboard"));
const ClientInvite = lazyWithRetry(() => import("./pages/ClientInvite"));
const AceitarConvite = lazyWithRetry(() => import("./pages/AceitarConvite"));

const TarefasAtrasadas = lazyWithRetry(() => import("./pages/TarefasAtrasadas"));

// Create QueryClient instance outside component to prevent recreation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 min "fresco" — não refaz na navegação
      gcTime: 5 * 60_000, // mantém em cache 5 min
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Security meta tags setup
const setupSecurityHeaders = () => {
  if (typeof document === 'undefined') return;
  
  // Content Security Policy - Allow Cal.com embed script and external navigation
  const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (!cspMeta) {
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    const csp = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://fonts.googleapis.com https://cdn.jsdelivr.net https://unpkg.com https://app.cal.com https://connect.facebook.net https://www.googletagmanager.com",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "img-src 'self' data: https: blob:",
      "connect-src 'self' https://tlvmfrqvnmcbwgkzmpjp.supabase.co wss://tlvmfrqvnmcbwgkzmpjp.supabase.co https://api.cal.com https://wa.me https://api.whatsapp.com https://web.whatsapp.com https://www.facebook.com https://connect.facebook.net https://*.google-analytics.com https://*.analytics.google.com https://*.googletagmanager.com",
      "object-src 'none'",
      "base-uri 'self'",
      "form-action 'self' https:"
    ].join("; ");
    meta.content = csp;
    document.head.appendChild(meta);
  }

  // X-Content-Type-Options
  const contentTypeMeta = document.querySelector('meta[http-equiv="X-Content-Type-Options"]');
  if (!contentTypeMeta) {
    const meta = document.createElement('meta');
    meta.httpEquiv = 'X-Content-Type-Options';
    meta.content = 'nosniff';
    document.head.appendChild(meta);
  }
};

// Setup security headers immediately
setupSecurityHeaders();

const AppContent: React.FC = () => {
  return (
    <>
      <NewVersionBanner />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GaRouteTracker />
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/termos-de-uso" element={<TermosDeUso />} />
            <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
            <Route path="/cookies" element={<Cookies />} />

            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/client-auth" element={<ClientAuth />} />
            <Route path="/client-invite/:token" element={<ClientInvite />} />
            <Route path="/client-portal" element={<ClientProtectedRoute><ClientPortal /></ClientProtectedRoute>} />
            <Route path="/client-event/:id" element={<ClientProtectedRoute><ClientEventDashboard /></ClientProtectedRoute>} />
            <Route path="/convite/:token" element={<AceitarConvite />} />
            <Route path="/portal/:token" element={<PortalGate><PortalCliente /></PortalGate>} />
            <Route path="/portal/:token/briefing" element={<PortalGate><PortalBriefing /></PortalGate>} />
            <Route path="/portal/:token/convidados" element={<PortalGate><PortalConvidados /></PortalGate>} />
            <Route path="/portal/:token/propostas" element={<PortalGate><PortalPropostas /></PortalGate>} />
            <Route path="/onboarding" element={<ProtectedRoute><FirstEventGate><Onboarding /></FirstEventGate></ProtectedRoute>} />
            <Route path="/dashboard-geral" element={<ProtectedRoute><SubscriptionGate><DashboardGeral /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/dashboard-eventos" element={<ProtectedRoute><SubscriptionGate><DashboardEventos /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/eventos" element={<ProtectedRoute><SubscriptionGate><Eventos /></SubscriptionGate></ProtectedRoute>} />
          
            {/* Novas páginas de gestão de eventos */}
            <Route path="/fornecedores-geral" element={<ProtectedRoute><SubscriptionGate><FornecedoresGeral /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/clientes" element={<ProtectedRoute><SubscriptionGate><Clientes /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/modelos" element={<ProtectedRoute><SubscriptionGate><Modelos /></SubscriptionGate></ProtectedRoute>} />
            
            {/* Rotas administrativas da empresa */}
            <Route path="/contratos" element={<ProtectedRoute><SubscriptionGate><Contratos /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/patrimonio" element={<ProtectedRoute><SubscriptionGate><Patrimonio /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/contas-pagar" element={<ProtectedRoute><SubscriptionGate><ContasPagar /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/contas-receber" element={<ProtectedRoute><SubscriptionGate><ContasReceber /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/tarefas-hoje" element={<ProtectedRoute><SubscriptionGate><TarefasHoje /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/tarefas-semana" element={<ProtectedRoute><SubscriptionGate><TarefasSemana /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/tarefas-atrasadas" element={<ProtectedRoute><SubscriptionGate><TarefasAtrasadas /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/reunioes" element={<ProtectedRoute><SubscriptionGate><Reunioes /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/orcamento-empresa" element={<ProtectedRoute><SubscriptionGate><OrcamentoEmpresa /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/vendas" element={<ProtectedRoute><SubscriptionGate><Vendas /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/crm" element={<ProtectedRoute><SubscriptionGate><CRM /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/configuracoes" element={<ProtectedRoute><Configuracoes /></ProtectedRoute>} />
            <Route path="/planos" element={<ProtectedRoute><Planos /></ProtectedRoute>} />
            <Route path="/feedback" element={<ProtectedRoute><Feedback etapa="inicio" /></ProtectedRoute>} />
            <Route path="/feedback/final" element={<ProtectedRoute><Feedback etapa="final" /></ProtectedRoute>} />
            <Route path="/admin/testadoras" element={<ProtectedRoute><AdminTestadoras /></ProtectedRoute>} />
            <Route path="/integracoes" element={<ProtectedRoute><SubscriptionGate><Integracoes /></SubscriptionGate></ProtectedRoute>} />
            <Route path="/notificacoes" element={<ProtectedRoute><Notificacoes /></ProtectedRoute>} />
            <Route path="/pagamentos" element={<ProtectedRoute><HistoricoPagamentos /></ProtectedRoute>} />
            
            {/* Rotas específicas para eventos - Todas dentro do EventLayout */}
            <Route path="/evento/:id/*" element={<ProtectedRoute><FirstEventRouteGate><EventLayout /></FirstEventRouteGate></ProtectedRoute>}>
              <Route index element={<EventoDashboard />} />
              <Route path="dashboard" element={<EventoDashboard />} />
              <Route path="detalhes" element={<DetalhesEvento />} />
              <Route path="tarefas" element={<EventoTarefas />} />
              <Route path="tarefas-hoje" element={<LegacyEventRedirect section="tarefas" tab="hoje" />} />
              <Route path="tarefas-semana" element={<LegacyEventRedirect section="tarefas" tab="semana" />} />
              <Route path="reunioes" element={<Reunioes />} />
              <Route path="orcamento" element={<EventoOrcamento />} />
              <Route path="orcamentos" element={<OrcamentosRedirect />} />
              <Route path="pagamentos-fornecedores" element={<EventoPagamentosFornecedores />} />
              <Route path="fornecedores" element={<Fornecedores />} />
              <Route path="documentos" element={<Documentos />} />
              <Route path="convidados" element={<EventoConvidados />} />
              <Route path="mesas" element={<LegacyEventRedirect section="convidados" tab="mesas" />} />
              <Route path="cronograma" element={<EventoCronograma />} />
              <Route path="lista-tarefas" element={<LegacyEventRedirect section="tarefas" tab="todas" />} />
              <Route path="cronograma-cerimonia" element={<LegacyEventRedirect section="cronograma" tab="cerimonia" />} />
              <Route path="cronograma-recepcao" element={<LegacyEventRedirect section="cronograma" tab="recepcao" />} />
              <Route path="briefing" element={<Briefing />} />
              <Route path="briefing/ficha" element={<BriefingFicha />} />
              <Route path="configuracoes" element={<Configuracoes />} />
              <Route path="notificacoes" element={<Notificacoes />} />
            </Route>
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ClientAuthProvider>
          <CompanyProvider>
            <AIChatProvider>
              <ThemeProvider
                attribute="class"
                defaultTheme="light"
                enableSystem={false}
                storageKey="theme"
              >
                <TooltipProvider>
                  <AppContent />
                </TooltipProvider>
              </ThemeProvider>
            </AIChatProvider>
          </CompanyProvider>
        </ClientAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;